// shared
// store
// models
// services
// utilities
// directives
import {AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {BehaviorSubject, Observable, Observer} from 'rxjs';
import {
  CompanyCategoryType,
  Company,
  PagedListOfCompany, CompanyCategory
} from '../../../services/api.client';

import {CompanyService} from '../../../services/CompanyService';
import {ModalComponent} from 'src/app/utilities/old-modal/components/modal';
import {AbstractControl, FormControl, UntypedFormControl} from '@angular/forms';
import {TableComponent} from '../../shared/table/TableComponent';
import {formatDate} from '../../../utilities/date-util';
import {CompanyCategoryService} from '../../../services/companyCategory.service';
import {Disposable} from '../../../utilities/Disposable';
import {map, takeUntil} from 'rxjs/operators';
import {ICompanyCategory} from '../../../shared/interfaces/ICompanyCategory';
import {CompanySelectedEvent} from '../../shared/events/company-selected-event';

@Component({
    selector: 'company-box',
    templateUrl: './CompanyListModalComponent.html',
    styleUrls: ['./CompanyListModalComponent.scss'],
    standalone: false
})

export class CompanyListModalComponent extends Disposable implements OnInit, AfterViewInit {

  constructor(private _companyService: CompanyService,
              private _companyCategoryService: CompanyCategoryService) {
    super();
    this.pagedList$ = this._pagedList$.asObservable();
    this.companies$ = this.pagedList$.pipe(map(x => x?.items));
    this.totalCount$ = this.pagedList$.pipe(map(x => x?.totalCount));
    this.currentDate = new Date().getDate();
    this.companyCategories$ = this._companyCategoryService.companyCategories$;
  }


  searchBox = new UntypedFormControl('');
  companyCategoryControl = new FormControl(null);

  headers = [
    {text: 'Company Name'},
    {text: 'Last Used'},
    {text: 'Category'}
  ];

  @ViewChild(ModalComponent)
  modal: ModalComponent;

  selectedRow: Company;
  searchValue = '';
  region = '';
  currentDate: any;
  showNoCompany = true;

  @Output() onCompanySelected = new EventEmitter<CompanySelectedEvent>();
  payee: AbstractControl;

  private observer: Observer<any>;

  // 003-retire-ngrx (US3): companies paged list went component-local — this
  // modal is the sole producer (its own getCompanies call) and consumer.
  private readonly _pagedList$ = new BehaviorSubject<PagedListOfCompany>(null);
  pagedList: PagedListOfCompany;
  pagedList$: Observable<PagedListOfCompany>;
  companies: Company[];
  companies$: Observable<Company[]>;
  companyCategories: CompanyCategory[];
  companyCategories$: Observable<CompanyCategory[]>;
  categoryTypesSelect: ICompanyCategory[];
  @Input() availableCategories$: Observable<CompanyCategory[]> | null;

  // PAGINATION
  limit = 10;
  currentPage = 1;
  maxSize = 1;
  totalCount: number;
  totalCount$: Observable<number>;

  @ViewChild(TableComponent)
  table: TableComponent;

  protected readonly Date = Date;

  ngOnInit(): void {
    this.pagedList$
        .pipe(takeUntil(this.$destroyed))
        .subscribe(pagedList => this.pagedList = pagedList);
    this.totalCount$
        .pipe(takeUntil(this.$destroyed))
        .subscribe(totalCount => this.totalCount = totalCount);
    this.companies$
        .pipe(takeUntil(this.$destroyed))
        .subscribe(companies => this.companies = companies);
  }

    ngAfterViewInit(): void {
        this.companyCategories$
            .pipe(takeUntil(this.$destroyed))
            .subscribe(companyCategories => {
                // If no availableCategories$ observable is provided, use all categories from store
                if (!this.availableCategories$) {
                    this.companyCategories = companyCategories;
                    this.populateCategoryTypesSelect();
                    // Re-filter companies when categories change
                    this.filterCompanies();
                }
            });

        if (this.availableCategories$) {
            this.availableCategories$
                .pipe(takeUntil(this.$destroyed))
                .subscribe(availableCategories => {
                    if (availableCategories && availableCategories.length > 0) {
                        this.companyCategories = availableCategories;
                        this.populateCategoryTypesSelect();
                        // Re-filter companies when available categories change
                        this.filterCompanies();
                    }
                });
        }
    }

  setLimit(limit: number): void {// # of rows per page
    this.limit = limit;
    this.filterCompanies();
  }

  setPage(page: number): void {
    this.currentPage = page;
    this.filterCompanies();
  }

  openModal(region: string = null, payee: AbstractControl = null, showNoCompany: boolean = true): Observable<boolean> {
    this.modal?.open();
    this.region = region;
    this.payee = payee;
    this.showNoCompany = showNoCompany;
    this.filterCompanies();
    return new Observable(obs => {
      this.observer = obs;
    });
  }

  filterCompanies(): void {// get companies
    const selectedCategoryValue = this.getCategory(this.companyCategoryControl?.value);
    let selectedCategory = [];
    if (selectedCategoryValue) {
        selectedCategory.push(selectedCategoryValue);
    }

    if (!selectedCategory || selectedCategory.length === 0) {
        selectedCategory = this.companyCategories.map(category => category.category);
    }

    const request = this._companyService.getCompanies(
        this.searchBox.value,
        this.region,
        selectedCategory,
        false,
        'LastUsedDate', // this.currentSortName,
        true, // this.currentSorting,
        this.limit,
        (this.currentPage - 1) * this.limit);
    this.table.request = request;
    request
        .pipe(takeUntil(this.$destroyed))
        .subscribe({
          next: pagedList => this._pagedList$.next(pagedList),
          error: () => {} // service already toasts; just don't emit
        });
  }

  setClickedRow(company: Company): void {
    this.selectedRow = company;
  }

  okClicked(company: Company): void {
    this._emitCloseEvent(company);
  }

  cancelClicked(): void {
    this._emitCloseEvent(null);
  }

  resetClicked(): void {
    this.selectedRow = null;
    this._emitCloseEvent(this.selectedRow, true);
  }

  getLastUsedDate(lastUsedDate: Date | null): string {
    if (!lastUsedDate) {
      return 'Never';
    // } else if (!lastUsedDate.isAfter(twelveMonthsAgo)) {
    //   return 'Over 12 Months Ago';
    } else {
      return formatDate(lastUsedDate, 'MM/DD/YYYY');
    }
  }

  resetFilters(): void {
    this.companyCategoryControl?.setValue(null);
    this.searchBox?.setValue('');
    this.filterCompanies();
  }

  private _emitCloseEvent(event: any, noCompany = false): void {
    this.onCompanySelected.emit({company: event, payee: this.payee, noCompany});
    this.modal.close();
    if (this.observer) {
      this.observer.next(event);
      this.observer.complete();
    }
  }

  private populateCategoryTypesSelect(): void {
    this.categoryTypesSelect = this.companyCategories.map(x => ({
      key: x.category.toString(),
      value: x.categoryLabel
    } as ICompanyCategory));
  }

  private getCategory(category: string): CompanyCategoryType {
    return CompanyCategoryType[category];
  }
}
